import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Heart, CheckCircle2, CreditCard, Smartphone, Building, ShieldCheck, Download } from "lucide-react";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const PURPOSES = ["Education", "Healthcare", "Environment", "Women Empowerment", "Nutrition", "Disability Support"];
const AMOUNTS = [51, 101, 501, 1001];
const GATEWAYS = [
  {
    id: "razorpay",
    label: "Razorpay",
    tagline: "India's most trusted gateway",
    icon: CreditCard,
    supports: ["Credit / Debit Card", "UPI", "Net Banking", "Wallets"],
  },
  {
    id: "phonepe",
    label: "PhonePe",
    tagline: "Fast UPI & PhonePe app checkout",
    icon: Smartphone,
    supports: ["UPI", "PhonePe Wallet", "Credit / Debit Card", "Net Banking"],
  },
  {
    id: "bank_transfer",
    label: "Bank Transfer",
    tagline: "Direct NEFT/RTGS to our SBI account",
    icon: Building,
    supports: ["NEFT", "RTGS", "IMPS"],
  },
];

export default function Donation() {
  const [step, setStep] = useState(1);
  const [purpose, setPurpose] = useState("Education");
  const [amount, setAmount] = useState(501);
  const [customAmount, setCustomAmount] = useState("");
  const [frequency, setFrequency] = useState("once");
  const [method, setMethod] = useState("razorpay");
  const [donor, setDonor] = useState({ donor_name: "", donor_email: "", donor_phone: "", message: "", pan: "", address: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const finalAmount = customAmount ? Number(customAmount) : amount;

  // Handle PhonePe redirect back
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const did = params.get("donation_id");
    if (did && params.get("phonepe")) {
      (async () => {
        try {
          const { data } = await api.post("/donations/verify", { donation_id: did, gateway_payment_id: "phonepe_redirect" });
          setSuccess({ donation: data.donation, note: data.thank_you_message });
          setStep(4);
          window.history.replaceState({}, "", "/donate");
        } catch (e) {
          toast.error(e?.response?.data?.detail || e?.response?.data?.message || "PhonePe verification failed");
        }
      })();
    }
  }, []);

  const submit = async () => {
    if (!donor.donor_name || !donor.donor_email) return toast.error("Please fill your details.");
    if (!finalAmount || finalAmount < 1) return toast.error("Enter a valid amount.");
    setLoading(true);
    try {
      const { data: createRes } = await api.post("/donations/create", {
        purpose, amount: finalAmount, payment_method: method, ...donor,
      });
      const gw = createRes.gateway || {};

      if (method === "razorpay" && !gw.mocked) {
        const ok = await loadRazorpayScript();
        if (!ok) { toast.error("Could not load Razorpay. Check your connection."); setLoading(false); return; }
        const rzp = new window.Razorpay({
          key: gw.key_id,
          amount: gw.amount_paise,
          currency: gw.currency || "INR",
          name: "Adopt Helpless Children Charitable Trust",
          description: `${purpose} · 80G eligible`,
          order_id: gw.razorpay_order_id,
          prefill: { name: donor.donor_name, email: donor.donor_email, contact: donor.donor_phone || "" },
          notes: { purpose, donation_id: createRes.donation.id },
          theme: { color: "#FF9933" },
          method: {
            upi: true,          // UPI apps (PhonePe, GPay, Paytm, BHIM, etc.)
            card: true,         // Credit / Debit cards
            netbanking: true,   // Net Banking (60+ banks)
            wallet: true,       // Mobile wallets (PhonePe, Freecharge, etc.)
            emi: true,          // Card / Cardless EMI
            paylater: true,     // LazyPay, Simpl, ICICI PayLater
          },
          config: {
            display: {
              blocks: {
                upi_apps: {
                  name: "Pay using UPI Apps",
                  instruments: [
                    { method: "upi", flows: ["intent", "collect", "qr"] },
                  ],
                },
                banks: {
                  name: "Pay using Net Banking",
                  instruments: [{ method: "netbanking" }],
                },
                other: {
                  name: "Other Payment Methods",
                  instruments: [{ method: "card" }, { method: "wallet" }, { method: "emi" }, { method: "paylater" }],
                },
              },
              sequence: ["block.upi_apps", "block.banks", "block.other"],
              preferences: { show_default_blocks: false },
            },
          },
          handler: async function (resp) {
            try {
              const { data: verifyRes } = await api.post("/donations/verify", {
                donation_id: createRes.donation.id,
                gateway_payment_id: resp.razorpay_payment_id,
                gateway_signature: resp.razorpay_signature,
              });
              setSuccess({ donation: verifyRes.donation, note: verifyRes.thank_you_message });
              setStep(4);
              toast.success("Donation successful — thank you!");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Verification failed. Contact us with your payment ID: " + resp.razorpay_payment_id);
            } finally { setLoading(false); }
          },
          modal: {
            ondismiss: () => { setLoading(false); toast.info("Payment cancelled."); },
          },
        });
        rzp.on("payment.failed", (r) => {
          toast.error("Payment failed: " + (r.error?.description || "Try again"));
          setLoading(false);
        });
        rzp.open();
        return;
      }

      if (method === "phonepe" && gw.redirect_url) {
        window.location.href = gw.redirect_url;
        return;
      }

      // Bank transfer / UPI fallback — manual confirmation for MVP
      const { data: verifyRes } = await api.post("/donations/verify", {
        donation_id: createRes.donation.id,
        gateway_payment_id: `manual_${Date.now()}`,
        gateway_signature: "",
      });
      setSuccess({ donation: verifyRes.donation, note: verifyRes.thank_you_message });
      setStep(4);
      toast.success("Thank you! Bank transfer instructions emailed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Donation failed. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fade-in max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-[0.22em] text-[#FF9933] font-bold mb-2">Donate</div>
        <h1 className="serif text-4xl md:text-5xl font-semibold text-[#000080]">Giving once · Giving monthly</h1>
        <p className="text-[#4A4A4A] mt-3">100% tax-deductible under 80G. Every rupee tracked.</p>
      </div>

      {step < 4 && (
        <div className="flex items-center justify-center gap-2 mb-10" data-testid="donate-stepper">
          {[1,2,3].map((s) => (
            <div key={s} className={`h-2 rounded-full transition-all ${s === step ? "w-16 bg-[#FF9933]" : s < step ? "w-8 bg-[#138808]" : "w-8 bg-[#eae5d9]"}`} />
          ))}
        </div>
      )}

      <div className="bg-white rounded-3xl border border-[#eae5d9] p-6 md:p-10">
        {step === 1 && (
          <div data-testid="donate-step-1">
            <h2 className="serif text-2xl text-[#000080] font-semibold mb-5">1. Choose your donation purpose</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {PURPOSES.map((p) => (
                <button key={p} onClick={() => setPurpose(p)} data-testid={`donate-purpose-${p.toLowerCase().replace(/\s+/g,'-')}`} className={`text-left p-4 rounded-xl border-2 transition-all ${purpose === p ? "border-[#FF9933] bg-[#FF9933]/5" : "border-[#eae5d9] hover:border-[#FF9933]/50"}`}>
                  <div className="font-semibold text-[#000080]">{p}</div>
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <Button onClick={() => setStep(2)} className="bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full px-7 h-11 btn-glow" data-testid="donate-next-1">Next →</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="donate-step-2">
            <h2 className="serif text-2xl text-[#000080] font-semibold mb-5">2. Select amount</h2>
            <div className="flex gap-2 mb-4">
              {["once","monthly"].map(f => (
                <button key={f} onClick={() => setFrequency(f)} data-testid={`donate-freq-${f}`} className={`px-4 py-2 rounded-full text-sm font-semibold ${frequency === f ? "bg-[#000080] text-white" : "bg-[#F3EFE7] text-[#4A4A4A]"}`}>
                  {f === "once" ? "One-Time" : "Monthly"}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {AMOUNTS.map((a) => (
                <button key={a} onClick={() => { setAmount(a); setCustomAmount(""); }} data-testid={`donate-amt-${a}`} className={`p-4 rounded-xl border-2 serif text-2xl font-semibold transition-all ${amount === a && !customAmount ? "border-[#FF9933] bg-[#FF9933] text-white" : "border-[#eae5d9] text-[#000080] hover:border-[#FF9933]/50"}`}>
                  ₹{a}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Input type="number" min="1" placeholder="Custom amount (₹)" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} data-testid="donate-custom" className="max-w-xs" />
              {customAmount && <span className="serif text-2xl font-semibold text-[#FF9933]">₹{customAmount}</span>}
            </div>
            <div className="mt-8 flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="donate-back-2" className="rounded-full">← Back</Button>
              <Button onClick={() => setStep(3)} className="bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full px-7 h-11 btn-glow" data-testid="donate-next-2">Next →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="donate-step-3">
            <h2 className="serif text-2xl text-[#000080] font-semibold mb-2">3. Choose Payment Gateway</h2>
            <p className="text-sm text-[#737373] mb-5">Pick a gateway — you'll choose Card, UPI, Net Banking or Wallet on the next screen.</p>
            <div className="grid md:grid-cols-3 gap-3 mb-6">
              {GATEWAYS.map((m) => {
                const active = method === m.id;
                return (
                  <button key={m.id} onClick={() => setMethod(m.id)} data-testid={`donate-method-${m.id}`}
                    className={`p-5 rounded-2xl border-2 text-left transition-all relative ${active ? "border-[#FF9933] bg-[#FF9933]/5 shadow-lg" : "border-[#eae5d9] hover:border-[#FF9933]/50 bg-white"}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-[#FF9933] text-white" : "bg-[#F3EFE7] text-[#FF9933]"}`}>
                        <m.icon className="w-5 h-5" strokeWidth={1.7} />
                      </div>
                      <div>
                        <div className="font-bold text-[#000080]">{m.label}</div>
                        <div className="text-[11px] text-[#737373]">{m.tagline}</div>
                      </div>
                      {active && <CheckCircle2 className="w-5 h-5 text-[#138808] ml-auto" />}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {m.supports.map(s => (
                        <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-[#eae5d9] text-[#4A4A4A] font-semibold">{s}</span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            {method === "bank_transfer" && (
              <div className="mb-6 p-4 rounded-xl bg-[#F3EFE7] border border-[#eae5d9] text-sm text-[#4A4A4A]" data-testid="bank-details">
                <div className="font-bold text-[#000080] mb-1">Bank Details</div>
                <div>Account Name: <b>Adopt Helpless Children Charitable Trust</b></div>
                <div>A/C No: <b>44703110576</b> · IFSC: <b>SBIN0018600</b> · Bank: State Bank of India</div>
                <div className="text-xs text-[#737373] mt-1">After transfer, click "Confirm Donation" below and email your transaction reference to Support@adopthelplesschildren.com — we'll issue your 80G receipt within 24 hrs.</div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              <Input required placeholder="Full Name" value={donor.donor_name} onChange={(e) => setDonor({ ...donor, donor_name: e.target.value })} data-testid="donate-name" />
              <Input required type="email" placeholder="Email" value={donor.donor_email} onChange={(e) => setDonor({ ...donor, donor_email: e.target.value })} data-testid="donate-email" />
              <Input placeholder="Phone (for receipt)" value={donor.donor_phone} onChange={(e) => setDonor({ ...donor, donor_phone: e.target.value })} data-testid="donate-phone" />
              <Input placeholder="PAN (optional, for 80G receipt)" value={donor.pan} onChange={(e) => setDonor({ ...donor, pan: e.target.value.toUpperCase() })} data-testid="donate-pan" maxLength={10} />
              <Textarea placeholder="A note to the team (optional)" value={donor.message} onChange={(e) => setDonor({ ...donor, message: e.target.value })} className="md:col-span-2" data-testid="donate-note" />
            </div>

            <div className="mt-6 p-4 rounded-xl bg-[#F3EFE7] border border-[#eae5d9] flex items-start gap-3 text-sm text-[#4A4A4A]">
              <ShieldCheck className="w-5 h-5 text-[#138808] shrink-0 mt-0.5" />
              <div>Secure checkout. 100% tax-deductible under Section 80G. You'll get a downloadable PDF receipt instantly.</div>
            </div>

            <div className="mt-8 flex justify-between items-center">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="donate-back-3" className="rounded-full">← Back</Button>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-xs text-[#737373]">You're donating</div>
                  <div className="serif text-2xl font-semibold text-[#FF9933]">₹{finalAmount}</div>
                </div>
                <Button onClick={submit} disabled={loading} className="bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full px-7 h-12 btn-glow" data-testid="donate-submit">
                  <Heart className="w-4 h-4 mr-2" />{loading ? "Processing..." : "Donate Now"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 4 && success && (
          <div className="text-center py-6" data-testid="donate-success">
            <div className="w-20 h-20 rounded-full bg-[#138808]/15 mx-auto flex items-center justify-center mb-5">
              <CheckCircle2 className="w-10 h-10 text-[#138808]" />
            </div>
            <h2 className="serif text-3xl text-[#000080] font-semibold">Thank you, {success.donation.donor_name}!</h2>
            <div className="serif text-5xl font-semibold text-[#FF9933] my-4">₹{success.donation.amount}</div>
            <p className="text-[#4A4A4A] max-w-xl mx-auto whitespace-pre-line">{success.note}</p>
            <div className="mt-3 text-xs text-[#737373]">
              Receipt No: <span className="font-mono font-semibold text-[#000080]">{success.donation.receipt_no}</span> · Donation ID: {success.donation.id}
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href={`${BACKEND_URL}/api/donations/${success.donation.id}/receipt`} target="_blank" rel="noreferrer" data-testid="donate-receipt">
                <Button className="bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full px-7 h-11 btn-glow">
                  <Download className="w-4 h-4 mr-2" /> Download 80G Receipt (PDF)
                </Button>
              </a>
              <Button onClick={() => { setStep(1); setSuccess(null); }} variant="outline" className="rounded-full border-[#000080] text-[#000080] hover:bg-[#000080] hover:text-white px-7 h-11" data-testid="donate-again">Donate Again</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
